import { quat, type Quat } from '../math/quat'
import { ONE_EURO_DEFAULTS, type OneEuroParams } from './one-euro'

function smoothingFactor(dt: number, cutoff: number): number {
  const r = 2 * Math.PI * cutoff * dt
  return r / (r + 1)
}

/**
 * One-Euro filter on the rotation manifold: derivative = angular speed (rad/s),
 * low-pass = slerp toward the new sample. Handles the q / -q double cover.
 */
export class OneEuroQuat {
  readonly params: OneEuroParams
  private q: Quat = [0, 0, 0, 1]
  private dq = 0 // filtered angular speed
  private hasValue = false
  private lastT: number | null = null

  constructor(params: Partial<OneEuroParams> = {}) {
    this.params = { ...ONE_EURO_DEFAULTS, ...params }
  }

  filter(out: Quat, sample: Quat, tSeconds: number): Quat {
    if (!this.hasValue || this.lastT === null) {
      quat.normalize(this.q, sample)
      this.hasValue = true
      this.lastT = tSeconds
      this.dq = 0
      return quat.copy(out, this.q)
    }
    let dt = tSeconds - this.lastT
    if (!(dt > 0)) dt = 1 / 60
    this.lastT = tSeconds
    const speed = quat.angle(this.q, sample) / dt
    const aD = smoothingFactor(dt, this.params.dCutoff)
    this.dq = aD * speed + (1 - aD) * this.dq
    const cutoff = this.params.minCutoff + this.params.beta * Math.abs(this.dq)
    const alpha = smoothingFactor(dt, cutoff)
    quat.slerp(this.q, this.q, sample, alpha)
    quat.normalize(this.q, this.q)
    return quat.copy(out, this.q)
  }

  get value(): Quat | null {
    return this.hasValue ? this.q : null
  }

  reset(): void {
    this.hasValue = false
    this.lastT = null
    this.dq = 0
  }
}
